import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import GuestOverlay from "@/components/auth/GuestOverlay";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useGenerationVip } from "@/hooks/useGeneration-vip";
import { supabase } from "@/integrations/supabase/client";
import { checkTaskStatus } from "@/lib/task-checker";
import {
  Video,
  Sparkles,
  Loader2,
  Coins,
  Crown,
  Download,
  History,
  ChevronDown,
  ChevronUp,
  FileText,
  AlertTriangle,
  Clock,
  RefreshCw,
  Trash2,
  Repeat,
  Globe,
  Users,
  Dog,
  Ghost,
  Wand2,
  Rocket,
  Heart,
  Flame
} from "lucide-react";

interface VipVideoLog {
  id: string;
  prompt: string;
  display_prompt?: string | null;
  is_professional?: boolean;
  is_magic?: boolean;
  result_url: string | null;
  status: string;
  created_at: string;
  model: string;
  credits_used?: number;
}

// Categories with characters
const CATEGORIES = [
  {
    id: "strange-people",
    label: "คนแปลก",
    labelEn: "Strange People",
    icon: Users,
    color: "from-purple-500/20 to-pink-500/20",
    characters: [
      { id: "wizard", label: "พ่อมด", labelEn: "Wizard" },
      { id: "alien", label: "มนุษย์ต่างดาว", labelEn: "Alien" },
      { id: "robot", label: "หุ่นยนต์", labelEn: "Robot" },
      { id: "superhero", label: "ซูเปอร์ฮีโร่", labelEn: "Superhero" },
      { id: "ninja", label: "นินจา", labelEn: "Ninja" },
      { id: "vampire", label: "แวมไพร์", labelEn: "Vampire" },
    ]
  },
  {
    id: "strange-animals",
    label: "สัตว์แปลก",
    labelEn: "Strange Animals",
    icon: Dog,
    color: "from-amber-500/20 to-orange-500/20",
    characters: [
      { id: "dragon", label: "มังกร", labelEn: "Dragon" },
      { id: "unicorn", label: "ยูนิคอร์น", labelEn: "Unicorn" },
      { id: "phoenix", label: "นกฟีนิกซ์", labelEn: "Phoenix" },
      { id: "griffin", label: "กริฟฟิน", labelEn: "Griffin" },
      { id: "kraken", label: "อสูรทะเล", labelEn: "Kraken" },
      { id: "cerberus", label: "หมา 3 หัว", labelEn: "Cerberus" },
    ]
  },
  {
    id: "fantasy",
    label: "แฟนตาซี",
    labelEn: "Fantasy",
    icon: Wand2,
    color: "from-blue-500/20 to-cyan-500/20",
    characters: [
      { id: "elf", label: "เอลฟ์", labelEn: "Elf" },
      { id: "dwarf", label: "คนแคระ", labelEn: "Dwarf" },
      { id: "fairy", label: "นางฟ้า", labelEn: "Fairy" },
      { id: "mermaid", label: "นางเงือก", labelEn: "Mermaid" },
      { id: "ogre", label: "ยักษ์", labelEn: "Ogre" },
      { id: "witch", label: "แม่มด", labelEn: "Witch" },
    ]
  },
  {
    id: "horror",
    label: "สยองขวัญ",
    labelEn: "Horror",
    icon: Ghost,
    color: "from-slate-500/20 to-gray-500/20",
    characters: [
      { id: "ghost", label: "ผี", labelEn: "Ghost" },
      { id: "zombie", label: "ซอมบี้", labelEn: "Zombie" },
      { id: "werewolf", label: "มนุษย์หมาป่า", labelEn: "Werewolf" },
      { id: "demon", label: "ปีศาจ", labelEn: "Demon" },
      { id: "skeleton", label: "โครงกระดูก", labelEn: "Skeleton" },
      { id: "mummy", label: "มัมมี่", labelEn: "Mummy" },
    ]
  },
  {
    id: "scifi",
    label: "ไซไฟ",
    labelEn: "Sci-Fi",
    icon: Rocket,
    color: "from-emerald-500/20 to-teal-500/20",
    characters: [
      { id: "cyborg", label: "ไซบอร์ก", labelEn: "Cyborg" },
      { id: "android", label: "แอนดรอยด์", labelEn: "Android" },
      { id: "space-marine", label: "ทหารอวกาศ", labelEn: "Space Marine" },
      { id: "mutant", label: "มนุษย์กลายพันธุ์", labelEn: "Mutant" },
      { id: "ai-entity", label: "AI มีชีวิต", labelEn: "AI Entity" },
      { id: "time-traveler", label: "นักเดินทางข้ามเวลา", labelEn: "Time Traveler" },
    ]
  },
  {
    id: "romance",
    label: "โรแมนติก",
    labelEn: "Romance",
    icon: Heart,
    color: "from-rose-500/20 to-pink-500/20",
    characters: [
      { id: "prince", label: "เจ้าชาย", labelEn: "Prince" },
      { id: "princess", label: "เจ้าหญิง", labelEn: "Princess" },
      { id: "knight", label: "อัศวิน", labelEn: "Knight" },
      { id: "dancer", label: "นักเต้น", labelEn: "Dancer" },
      { id: "musician", label: "นักดนตรี", labelEn: "Musician" },
      { id: "artist", label: "ศิลปิน", labelEn: "Artist" },
    ]
  },
];

// Languages
const LANGUAGES = [
  { id: "th", label: "ไทย", labelEn: "Thai", flag: "🇹🇭" },
  { id: "en", label: "อังกฤษ", labelEn: "English", flag: "🇺🇸" },
  { id: "hi", label: "ฮินดี", labelEn: "Hindi", flag: "🇮🇳" },
  { id: "zh", label: "จีน", labelEn: "Chinese", flag: "🇨🇳" },
  { id: "ja", label: "ญี่ปุ่น", labelEn: "Japanese", flag: "🇯🇵" },
  { id: "ko", label: "เกาหลี", labelEn: "Korean", flag: "🇰🇷" },
  { id: "es", label: "สเปน", labelEn: "Spanish", flag: "🇪🇸" },
  { id: "fr", label: "ฝรั่งเศส", labelEn: "French", flag: "🇫🇷" },
  { id: "de", label: "เยอรมัน", labelEn: "German", flag: "🇩🇪" },
  { id: "pt", label: "โปรตุเกส", labelEn: "Portuguese", flag: "🇧🇷" },
  { id: "ru", label: "รัสเซีย", labelEn: "Russian", flag: "🇷🇺" },
  { id: "ar", label: "อาหรับ", labelEn: "Arabic", flag: "🇸🇦" },
];

const CREDITS_REQUIRED = 70;

const Professional = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, profile, loading: authLoading } = useAuth();
  const { generate, isGenerating, userCredits } = useGenerationVip();

  // State
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCharacter, setSelectedCharacter] = useState<string | null>(null);
  const [additionalScript, setAdditionalScript] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("th");
  const [isScriptOpen, setIsScriptOpen] = useState(false);
  const [recentVideos, setRecentVideos] = useState<VipVideoLog[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState<string | null>(null);

  // Get current category data
  const currentCategory = CATEGORIES.find(c => c.id === selectedCategory);
  const currentCharacter = currentCategory?.characters.find(ch => ch.id === selectedCharacter);
  const currentLanguage = LANGUAGES.find(l => l.id === selectedLanguage);

  // Fetch recent professional videos
  const fetchRecentVideos = async () => {
    if (!user) return;
    setLoadingRecent(true);
    try {
      const { data, error } = await (supabase
        .from("generation_logs") as any)
        .select("id, prompt, display_prompt, is_professional, is_magic, result_url, status, created_at, model, credits_used")
        .eq("user_id", user.id)
        .eq("is_professional", true)
        .order("created_at", { ascending: false })
        .limit(6);

      if (error) throw error;
      setRecentVideos(data as any as VipVideoLog[]);
    } catch (err) {
      console.error("Error fetching recent videos:", err);
    } finally {
      setLoadingRecent(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchRecentVideos();
    }
  }, [user]);

  // Handle category change - reset character
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedCharacter(null);
  };

  // Build prompt from selections
  const buildPrompt = () => {
    const parts = [];

    if (currentCategory) {
      parts.push(`Category: ${currentCategory.labelEn}`);
    }
    if (currentCharacter) {
      parts.push(`Character: ${currentCharacter.labelEn}`);
    }
    if (additionalScript.trim()) {
      parts.push(`Script: ${additionalScript.trim()}`);
    }
    if (currentLanguage) {
      parts.push(`Language: ${currentLanguage.labelEn}`);
    }

    return parts.join(". ");
  };

  const handleGenerate = async () => {
    if (!selectedCategory) {
      alert(t("กรุณาเลือกหมวดหมู่", "Please select a category"));
      return;
    }

    if (!selectedCharacter) {
      alert(t("กรุณาเลือกตัวละคร", "Please select a character"));
      return;
    }

    const prompt = buildPrompt();
    const displayPrompt = `${currentCategory?.label || ""} - ${currentCharacter?.label || ""}${additionalScript ? ` (${additionalScript})` : ""}`;

    const result = await generate(
      displayPrompt,
      prompt,
      CREDITS_REQUIRED,
      {
        category: selectedCategory,
        character: selectedCharacter,
        language: selectedLanguage,
        additionalScript: additionalScript,
        skipPolling: true
      } as any
    );

    if (result.success) {
      setTimeout(() => {
        fetchRecentVideos();
      }, 1000);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">{t("สำเร็จ", "Completed")}</Badge>;
      case "processing":
        return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 animate-pulse">{t("กำลังสร้าง...", "Generating...")}</Badge>;
      case "pending":
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">{t("รอดำเนินการ", "Pending")}</Badge>;
      case "failed":
        return <Badge variant="destructive" className="bg-red-500/10 text-red-500 border-red-500/20">{t("ล้มเหลว", "Failed")}</Badge>;
      default:
        return null;
    }
  };

  const handleCheckStatus = async (logId: string) => {
    if (checkingStatus === logId) return;
    setCheckingStatus(logId);
    try {
      const result = await checkTaskStatus(logId);
      if (result.success && (result.status === "completed" || result.status === "failed")) {
        fetchRecentVideos();
      }
    } catch (error) {
      console.error("Error checking status:", error);
    } finally {
      setCheckingStatus(null);
    }
  };

  const getDisplayPrompt = (log: any) => {
    return log.display_prompt || log.prompt || "";
  };

  const formatDateHistory = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDeleteHistory = async (id: string) => {
    try {
      if (!confirm(t("คุณแน่ใจหรือไม่ที่จะลบรายการนี้?", "Are you sure you want to delete this item?"))) {
        return;
      }

      const { data: success, error } = await supabase.rpc('delete_generation_log' as any, { p_log_id: id });

      if (error) {
        const { error: dbError } = await (supabase
          .from("generation_logs") as any)
          .delete()
          .eq("id", id);

        if (dbError) throw dbError;
      }

      fetchRecentVideos();
    } catch (error: any) {
      console.error("Error deleting log:", error);
    }
  };

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.open(url, '_blank');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Crown className="h-10 w-10 text-amber-500" />
              <h1 className="text-4xl font-heading font-bold">
                {t("สร้างวิดีโอ AI", "AI Video Generator")}
              </h1>
            </div>
            <p className="text-muted-foreground text-lg">
              {t("เลือกหมวดหมู่และตัวละคร แล้วกด Generate ได้เลย!", "Select category and character, then Generate!")}
            </p>

            {/* Credits Display */}
            <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
              <Coins className="h-5 w-5 text-primary" />
              <span className="text-sm text-muted-foreground">{t("เครดิตคงเหลือ", "Credits")}:</span>
              <span className="text-lg font-bold text-primary">{(userCredits || 0).toLocaleString()}</span>
            </div>
          </div>

          {/* Main Generation Card */}
          <Card className="rounded-3xl border border-white/20 shadow-2xl bg-white/60 backdrop-blur-xl dark:bg-black/40 overflow-hidden">
            <CardContent className="p-6 md:p-8 space-y-8">

              {/* Step 1: Category Selection */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm">1</div>
                  <h3 className="text-lg font-semibold">{t("เลือกหมวดหมู่", "Select Category")}</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {CATEGORIES.map((category) => {
                    const Icon = category.icon;
                    return (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryChange(category.id)}
                        disabled={isGenerating}
                        className={`relative p-4 rounded-2xl border-2 transition-all text-left overflow-hidden group ${
                          selectedCategory === category.id
                            ? "border-primary bg-primary/10 ring-2 ring-primary/30 scale-[1.02]"
                            : "border-border hover:border-primary/50 hover:bg-muted/30"
                        } ${isGenerating ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-50 group-hover:opacity-70 transition-opacity`} />
                        <div className="relative z-10 flex items-center gap-3">
                          <Icon className={`h-6 w-6 ${selectedCategory === category.id ? "text-primary" : "text-muted-foreground"}`} />
                          <div>
                            <p className="font-semibold text-sm">{category.label}</p>
                            <p className="text-xs text-muted-foreground">{category.labelEn}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2: Character Selection */}
              <div className={`space-y-4 transition-all ${!selectedCategory ? "opacity-40 pointer-events-none" : ""}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${selectedCategory ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>2</div>
                  <h3 className="text-lg font-semibold">{t("เลือกตัวละคร", "Select Character")}</h3>
                </div>

                {currentCategory && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {currentCategory.characters.map((character) => (
                      <button
                        key={character.id}
                        onClick={() => setSelectedCharacter(character.id)}
                        disabled={isGenerating}
                        className={`p-4 rounded-xl border-2 transition-all text-center ${
                          selectedCharacter === character.id
                            ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                            : "border-border hover:border-primary/50 hover:bg-muted/30"
                        } ${isGenerating ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <p className="font-semibold">{character.label}</p>
                        <p className="text-xs text-muted-foreground">{character.labelEn}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Step 3: Additional Script (Collapsible) */}
              <div className={`space-y-4 transition-all ${!selectedCharacter ? "opacity-40 pointer-events-none" : ""}`}>
                <Collapsible open={isScriptOpen} onOpenChange={setIsScriptOpen}>
                  <CollapsibleTrigger asChild>
                    <button className="flex items-center gap-2 w-full text-left group">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${selectedCharacter ? "bg-muted text-muted-foreground" : "bg-muted text-muted-foreground"}`}>
                        <FileText className="h-4 w-4" />
                      </div>
                      <h3 className="text-lg font-semibold flex-1">{t("บทละครเพิ่มเติม", "Additional Script")}</h3>
                      <Badge variant="outline" className="text-xs">{t("ไม่บังคับ", "Optional")}</Badge>
                      {isScriptOpen ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground transition-transform" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform" />
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <textarea
                      value={additionalScript}
                      onChange={(e) => setAdditionalScript(e.target.value)}
                      disabled={isGenerating}
                      placeholder={t(
                        "เช่น กำลังเดินอยู่ในป่าลึก มีแสงไฟสีฟ้าล้อมรอบ...",
                        "e.g., Walking in a deep forest with blue lights surrounding..."
                      )}
                      rows={3}
                      className={`w-full px-4 py-3 rounded-xl border-2 bg-background transition-all resize-none ${
                        additionalScript
                          ? "border-primary/50 bg-primary/5"
                          : "border-border"
                      } ${isGenerating ? "opacity-50 cursor-not-allowed" : ""}`}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {t("ระบุรายละเอียดเพิ่มเติมเพื่อให้ผลลัพธ์ตรงใจมากขึ้น", "Add more details for better results")}
                    </p>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              {/* Step 4: Language Selection */}
              <div className={`space-y-4 transition-all ${!selectedCharacter ? "opacity-40 pointer-events-none" : ""}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${selectedCharacter ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>3</div>
                  <h3 className="text-lg font-semibold">{t("เลือกภาษา", "Select Language")}</h3>
                </div>

                <Select value={selectedLanguage} onValueChange={setSelectedLanguage} disabled={isGenerating}>
                  <SelectTrigger className="w-full h-12 rounded-xl border-2 text-base">
                    <SelectValue>
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{currentLanguage?.flag}</span>
                        <span>{currentLanguage?.label}</span>
                        <span className="text-muted-foreground">({currentLanguage?.labelEn})</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.id} value={lang.id} className="py-3">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{lang.flag}</span>
                          <span className="font-medium">{lang.label}</span>
                          <span className="text-muted-foreground">({lang.labelEn})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selection Summary */}
              {selectedCategory && selectedCharacter && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-primary/10 to-amber-500/10 border border-primary/20">
                  <p className="text-sm font-medium mb-2">{t("สรุป", "Summary")}</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="text-sm py-1">
                      {currentCategory?.label}
                    </Badge>
                    <Badge variant="secondary" className="text-sm py-1">
                      {currentCharacter?.label}
                    </Badge>
                    {additionalScript && (
                      <Badge variant="outline" className="text-sm py-1 max-w-[200px] truncate">
                        {additionalScript}
                      </Badge>
                    )}
                    <Badge className="text-sm py-1 bg-primary/20 text-primary border-primary/30">
                      {currentLanguage?.flag} {currentLanguage?.label}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Credit Warning */}
              {user && userCredits < CREDITS_REQUIRED && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-destructive/10 border border-destructive/20">
                  <Flame className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-destructive">
                      {t("เครดิตไม่เพียงพอ", "Insufficient Credits")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        `ต้องการ ${CREDITS_REQUIRED} เครดิต แต่คุณมี ${userCredits.toLocaleString()} เครดิต`,
                        `Need ${CREDITS_REQUIRED} credits but you have ${userCredits.toLocaleString()}`
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Guest Overlay or Generate Button */}
              {!user ? (
                <div className="relative min-h-[150px] rounded-2xl overflow-hidden border border-white/20">
                  <GuestOverlay
                    title={t("เข้าสู่ระบบเพื่อสร้างวิดีโอ", "Login to Generate Videos")}
                    description={t("สมัครสมาชิกเพื่อเข้าถึงฟีเจอร์ทั้งหมด", "Register to access all features")}
                    className="rounded-none border-none"
                    showIcon={true}
                  />
                </div>
              ) : (
                <Button
                  variant="hero"
                  size="lg"
                  className="w-full h-14 text-lg rounded-xl gap-3"
                  onClick={handleGenerate}
                  disabled={isGenerating || !selectedCategory || !selectedCharacter || userCredits < CREDITS_REQUIRED}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-6 w-6 animate-spin" />
                      {t("กำลังสร้าง...", "Generating...")}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-6 w-6" />
                      {t("สร้างวิดีโอ", "Generate Video")}
                      <Badge variant="secondary" className="ml-2 bg-white/20">
                        {CREDITS_REQUIRED} {t("เครดิต", "credits")}
                      </Badge>
                    </>
                  )}
                </Button>
              )}

              {user && (
                <p className="text-xs text-center text-muted-foreground">
                  {t(
                    "หลังกดสร้าง รอประมาณ 4-6 นาที แล้วรีเฟรชหน้าเว็บเพื่อดูผลลัพธ์",
                    "After generating, wait 4-6 minutes then refresh to see results"
                  )}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recent Professional Videos */}
          {user && recentVideos.length > 0 && (
            <div className="mt-12">
              <Card className="rounded-3xl border border-white/20 shadow-xl bg-white/50 backdrop-blur-md dark:bg-black/40 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-heading font-bold flex items-center gap-2">
                      <Crown className="h-5 w-5 text-amber-500" />
                      {t("วิดีโอล่าสุด", "Recent Videos")}
                    </h2>
                    <p className="text-sm text-muted-foreground">{t("วิดีโอที่คุณสร้าง", "Your generated videos")}</p>
                  </div>
                  <Link to="/history/video">
                    <Button variant="outline" className="gap-2">
                      <History className="h-4 w-4" />
                      {t("ดูทั้งหมด", "View All")}
                    </Button>
                  </Link>
                </div>

                {loadingRecent ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {recentVideos.map((video) => {
                      let videoUrl = video.result_url || "";
                      if (videoUrl.startsWith("{")) {
                        try {
                          const parsed = JSON.parse(videoUrl);
                          videoUrl = parsed.resultUrls?.[0] || parsed.url || "";
                        } catch {}
                      } else if (videoUrl.includes(",")) {
                        videoUrl = videoUrl.split(",")[0].trim();
                      }

                      const isCompleted = video.status === "completed" && videoUrl && videoUrl.startsWith("http");
                      const isFailed = video.status === "failed";
                      const canCheckStatus = !isCompleted && !isFailed;

                      return (
                        <Card
                          key={video.id}
                          className="overflow-hidden border border-amber-500/30 shadow-lg bg-white/80 backdrop-blur-md dark:bg-black/60 hover:border-amber-500/50 transition-all rounded-2xl"
                        >
                          {/* Video Frame */}
                          <div className="p-3">
                            <div className="relative w-full rounded-xl overflow-hidden shadow-md border border-black/5 bg-black/5">
                              {isCompleted && videoUrl ? (
                                <video
                                  src={videoUrl}
                                  className="w-full h-auto max-h-[250px] block bg-black/10"
                                  controls
                                  playsInline
                                  preload="metadata"
                                />
                              ) : (
                                <div className="w-full aspect-video flex flex-col items-center justify-center bg-gradient-to-br from-amber-500/10 to-orange-500/10 gap-2">
                                  {isFailed ? (
                                    <>
                                      <AlertTriangle className="h-8 w-8 text-destructive" />
                                      <p className="text-xs text-destructive">{t("ผิดพลาด", "Failed")}</p>
                                    </>
                                  ) : (
                                    <>
                                      <Clock className="h-8 w-8 text-muted-foreground animate-pulse" />
                                      <p className="text-xs text-muted-foreground">{t("กำลังสร้าง...", "Generating...")}</p>
                                      {canCheckStatus && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleCheckStatus(video.id)}
                                          disabled={checkingStatus === video.id}
                                          className="mt-2"
                                        >
                                          {checkingStatus === video.id ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                          ) : (
                                            <>
                                              <RefreshCw className="h-4 w-4 mr-1" />
                                              {t("เช็กสถานะ", "Check")}
                                            </>
                                          )}
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <CardContent className="p-4 pt-0">
                            <p className="text-sm font-medium line-clamp-1">{getDisplayPrompt(video)}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-muted-foreground">{formatDateHistory(video.created_at)}</span>
                              {getStatusBadge(video.status)}
                            </div>

                            <div className="flex items-center justify-end gap-1 mt-3">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                onClick={() => handleDeleteHistory(video.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              {isCompleted && videoUrl && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-primary hover:bg-primary/10"
                                  onClick={() => handleDownload(videoUrl, `video-${video.id.slice(0, 8)}.mp4`)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Professional;
